// Claims to review — pure helpers. No state, no side effects.
import type { Claim, ClaimSummary, FileBy, Mode, Procedure, QueueGroup, RowTag } from './types';

export const CLAIMS_USER = { name: 'Ava Thompson', role: 'admin' } as const;
export const PRACTICE = 'Alamo Heights Dental';
export { PMS } from '@/features/shared/pms';
export const TRAINING_GOAL = 200;

const COUNT_WORDS = [
  'Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
];

/** Count spelled as a word up to ten, else the numeral. */
export function countWord(n: number): string {
  return n >= 0 && n <= 10 ? (COUNT_WORDS[n] ?? String(n)) : String(n);
}

/** "Today" per mode (§14). */
export function todayIso(mode: Mode): string {
  return mode === 'autopilot' ? '2026-11-18' : '2026-10-18';
}

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function parseIso(iso: string): { y: number; m: number; d: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return null;
  return { y: Number(match[1]), m: Number(match[2]), d: Number(match[3]) };
}

/** "Oct 12, 2026" */
export function formatDateFull(iso: string): string {
  const p = parseIso(iso);
  if (!p) return iso;
  return `${MONTHS[p.m - 1] ?? ''} ${String(p.d)}, ${String(p.y)}`;
}

/** "Oct 12" */
export function formatDateShort(iso: string): string {
  const p = parseIso(iso);
  if (!p) return iso;
  return `${MONTHS[p.m - 1] ?? ''} ${String(p.d)}`;
}

/** Whole-dollar currency with grouping: "$1,580". */
export function formatMoney(n: number): string {
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

/** Billed = sum of procedure fees (§13.2). */
export function billedOf(claim: Pick<Claim, 'procedures'>): number {
  return claim.procedures.reduce((sum, p) => sum + (p.fee || 0), 0);
}

/** Codes in claim order, de-duplicated. */
export function codesOf(procedures: Procedure[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const p of procedures) {
    if (!seen.has(p.code)) {
      seen.add(p.code);
      out.push(p.code);
    }
  }
  return out;
}

export function hasUnresolvedDecision(claim: Claim): boolean {
  return !!claim.decision && !claim.decision.chosen;
}

/** The queue group a claim belongs to (§5.2 / §11.3). */
export function groupOf(claim: Claim, mode: Mode): QueueGroup {
  if (mode === 'autopilot') return 'waiting';
  if (claim.held) return 'on_hold';
  if (hasUnresolvedDecision(claim)) return 'needs_decision';
  return 'ready';
}

/** Exactly one tag per row, by priority (§5.4 / §11.3). */
export function tagOf(claim: Claim, mode: Mode): RowTag {
  if (mode === 'autopilot') return 'needs_you';
  if (claim.held) return 'on_hold';
  if (hasUnresolvedDecision(claim)) return 'needs_you';
  if (claim.source.kind === 'elva_created') return 'elva_created';
  if (claim.changes.length > 0) return 'elva_changed';
  return 'no_changes';
}

export const TAG_LABEL: Record<RowTag, string> = {
  on_hold: 'On hold',
  needs_you: 'Needs you',
  elva_created: 'ELVA created',
  elva_changed: 'ELVA changed',
  no_changes: 'No changes',
};

/** Badge variant class suffix used in claims.css (b-mute / b-warn / b-info). */
export const TAG_BADGE: Record<RowTag, 'b-mute' | 'b-warn' | 'b-info'> = {
  on_hold: 'b-mute',
  needs_you: 'b-warn',
  elva_created: 'b-info',
  elva_changed: 'b-info',
  no_changes: 'b-mute',
};

/** The list line shown after the tag. */
export function listLineOf(claim: Claim, mode: Mode): string {
  if (mode === 'autopilot') return claim.heldShort ?? claim.listLine;
  if (claim.held) {
    const until = claim.held.until ? `Until ${formatDateShort(claim.held.until)} · ` : '';
    return `${until}${claim.held.reason}`;
  }
  return claim.listLine;
}

/** Whole days from one yyyy-mm-dd to another (b minus a). */
export function daysBetween(aIso: string, bIso: string): number {
  const a = parseIso(aIso);
  const b = parseIso(bIso);
  if (!a || !b) return 0;
  return Math.round((Date.UTC(b.y, b.m - 1, b.d) - Date.UTC(a.y, a.m - 1, a.d)) / 86_400_000);
}

/** A claim within this many days of the payer's filing limit shows its countdown (D237). */
export const FILING_WARN_DAYS = 14;

/**
 * "File by Nov 4 · 9 days" for a claim within 14 days of the payer's limit, else nothing
 * (past the limit is a question, Q12, not a countdown). "· not confirmed" when the payer's
 * limit isn't researched. Days run from the page's "today" for the mode.
 */
export function fileByOf(claim: Claim, mode: Mode): FileBy | undefined {
  const tf = claim.timelyFiling;
  if (!tf) return undefined;
  const days = daysBetween(todayIso(mode), tf.limitIso);
  if (days < 0 || days > FILING_WARN_DAYS) return undefined;
  const when = days === 0 ? 'today' : `${String(days)} ${days === 1 ? 'day' : 'days'}`;
  const base = `File by ${formatDateShort(tf.limitIso)} · ${when}`;
  return { days, label: tf.confirmed ? base : `${base} · not confirmed` };
}

/**
 * Queue order inside a group: a claim near its filing limit first (fewest days first), then
 * oldest service date, then higher billed amount (§5.2, D237).
 */
export function compareSummaries(a: ClaimSummary, b: ClaimSummary): number {
  if (a.fileBy || b.fileBy) {
    if (!a.fileBy) return 1;
    if (!b.fileBy) return -1;
    if (a.fileBy.days !== b.fileBy.days) return a.fileBy.days - b.fileBy.days;
  }
  if (a.serviceDate !== b.serviceDate) return a.serviceDate < b.serviceDate ? -1 : 1;
  return b.billed - a.billed;
}

/** Oldest service date first, then higher billed amount first (§5.2). */
export function queueSort(a: Claim, b: Claim): number {
  if (a.serviceDate !== b.serviceDate) return a.serviceDate < b.serviceDate ? -1 : 1;
  return billedOf(b) - billedOf(a);
}

export function toSummary(claim: Claim, mode: Mode): ClaimSummary {
  const fileBy = fileByOf(claim, mode);
  return {
    id: claim.id,
    patient: claim.patient,
    memberId: claim.memberId,
    payerShort: claim.payer.short,
    codes: codesOf(claim.procedures),
    billed: billedOf(claim),
    serviceDate: claim.serviceDate,
    tag: tagOf(claim, mode),
    group: groupOf(claim, mode),
    listLine: listLineOf(claim, mode),
    ...(fileBy ? { fileBy } : {}),
  };
}

/** Group order for the queue (§5.2). */
export const GROUP_ORDER: QueueGroup[] = ['needs_decision', 'ready', 'on_hold', 'waiting'];

export const GROUP_LABEL: Record<QueueGroup, string> = {
  needs_decision: 'Needs your decision',
  ready: 'Ready to confirm',
  on_hold: 'On hold',
  waiting: 'Waiting for you',
};

/** Procedure group = group of the highest-fee procedure (§14.1). */
export function procedureGroupOf(procedures: Procedure[], codeGroup: Record<string, string>): string {
  let best: Procedure | null = null;
  for (const p of procedures) {
    if (!best || p.fee > best.fee) best = p;
  }
  return best ? codeGroup[best.code] ?? '' : '';
}

/** Case-insensitive search over patient, claim number, payer, member id, codes
 *  and the billed amount (rcm-02 Fix-on-staging; §5.1 / §9). */
export function matchesQuery(s: ClaimSummary, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if (s.patient.toLowerCase().includes(q)) return true;
  if (s.id.toLowerCase().includes(q)) return true;
  if (s.payerShort.toLowerCase().includes(q)) return true;
  if (s.memberId.toLowerCase().includes(q)) return true;
  if (s.codes.some((c) => c.toLowerCase().includes(q))) return true;
  // Amount: "1580", "1580.00" and "$1,580" all reach the billed figure.
  const amount = `${String(s.billed)} ${s.billed.toFixed(2)} ${formatMoney(s.billed)}`.toLowerCase();
  return amount.includes(q);
}

/** A short wall-clock time like "10:42 AM" for history/action stamps (mock). */
export function clockNow(): string {
  const d = new Date();
  let h = d.getHours();
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  const m = d.getMinutes().toString().padStart(2, '0');
  return `${String(h)}:${m} ${ampm}`;
}
